import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Grid, Card, CardContent, Alert,
  Checkbox, FormControlLabel, Toolbar, Chip, TextField,
} from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';
import api from '../api';
import { navyBlue } from '../constants/colors';
import { useThemeColors } from '../hooks/useThemeColors';

const PRINT_PAGE_STYLE_ID = 'qr-print-page-style';

// Office assets print as the black 4x6 card
const isOfficeCard = (qr) => qr.location === 'Office';

export default function QRCodePrint() {
  const [qrCodes, setQRCodes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAssets, setSelectedAssets] = useState([]);
  const { borderColor, accentBlue, gradientPrimary, gradientPrimaryHover } = useThemeColors();

  useEffect(() => {
    loadQRCodes();
    loadLocations();
  }, []);

  const loadQRCodes = async () => {
    try {
      const response = await api.post('/assets/qrcodes/bulk');
      setQRCodes(response.data);
      setError('');
    } catch (err) {
      setError('Failed to generate QR codes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadLocations = async () => {
    try {
      const response = await api.get('/locations');
      setLocations(response.data);
    } catch (err) {
      console.error('Failed to load locations:', err);
    }
  };

  // An all-Office print goes two 4x6 cards to a landscape Letter sheet; any
  // print with standard cards in it stays portrait so those keep their layout.
  // Setup is undone once the print dialog closes.
  const printCards = (cards, selectedOnly) => {
    document.getElementById(PRINT_PAGE_STYLE_ID)?.remove();
    document.body.classList.toggle('print-selected-only', selectedOnly);
    if (cards.length > 0 && cards.every(isOfficeCard)) {
      const pageStyle = document.createElement('style');
      pageStyle.id = PRINT_PAGE_STYLE_ID;
      pageStyle.textContent = '@page { size: landscape; }';
      document.head.appendChild(pageStyle);
    }
    window.addEventListener('afterprint', () => {
      document.body.classList.remove('print-selected-only');
      document.getElementById(PRINT_PAGE_STYLE_ID)?.remove();
    }, { once: true });
    window.print();
  };

  const handlePrint = () => {
    printCards(filteredQRCodes, false);
  };

  const handleToggleSelect = (assetId) => {
    setSelectedAssets(prev =>
      prev.includes(assetId)
        ? prev.filter(id => id !== assetId)
        : [...prev, assetId]
    );
  };

  const handleSelectAll = () => {
    if (selectedAssets.length === filteredQRCodes.length) {
      setSelectedAssets([]);
    } else {
      setSelectedAssets(filteredQRCodes.map(qr => qr.id));
    }
  };

  const handlePrintSelected = () => {
    if (selectedAssets.length === 0) {
      setError('Please select at least one QR code to print');
      return;
    }
    printCards(qrCodes.filter(qr => selectedAssets.includes(qr.id)), true);
  };

  // Get unique categories (based on current location filter)
  const getUniqueCategoriesForLocation = () => {
    let filtered = qrCodes;
    if (filterLocation === 'not_assigned') {
      filtered = qrCodes.filter(qr => !qr.location);
    } else if (filterLocation) {
      filtered = qrCodes.filter(qr => qr.location === filterLocation);
    }
    return [...new Set(filtered.map(qr => qr.category).filter(Boolean))].sort();
  };

  const uniqueCategories = getUniqueCategoriesForLocation();

  // Get location counts
  const getLocationCounts = () => {
    const counts = { all: qrCodes.length };
    const unassignedCount = qrCodes.filter(qr => !qr.location).length;
    counts.not_assigned = unassignedCount;
    locations.forEach(location => {
      counts[location.name] = qrCodes.filter(qr => qr.location === location.name).length;
    });
    return counts;
  };

  const locationCounts = getLocationCounts();

  // Filter QR codes by location, category, and search
  const filteredQRCodes = qrCodes.filter(qr => {
    // Location filter
    if (filterLocation === 'not_assigned' && qr.location) {
      return false;
    }
    if (filterLocation && filterLocation !== 'not_assigned' && qr.location !== filterLocation) {
      return false;
    }
    // Category filter
    if (filterCategory && qr.category !== filterCategory) {
      return false;
    }
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        qr.asset_number?.toLowerCase().includes(search) ||
        qr.asset_description?.toLowerCase().includes(search) ||
        qr.category?.toLowerCase().includes(search) ||
        qr.location?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  if (loading) return <Typography>Loading QR codes...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, '@media print': { display: 'none' } }}>
        <Typography variant="h4" sx={{ color: accentBlue, fontWeight: 600 }}>QR Codes</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrintSelected}
            sx={{
              borderColor: navyBlue,
              color: accentBlue,
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            Print Selected ({selectedAssets.length})
          </Button>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{
              background: gradientPrimary,
              borderRadius: 2,
              textTransform: 'none',
              '&:hover': {
                background: gradientPrimaryHover,
              },
            }}
          >
            Print All
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Location Filter - Bubble chips */}
      <Paper sx={{ mb: 2, p: 2, borderRadius: 2, border: `1px solid ${borderColor}`, '@media print': { display: 'none' } }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, color: accentBlue, fontWeight: 600 }}>
          Filter by Location
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip
            label={`All Locations (${locationCounts.all})`}
            onClick={() => {
              setFilterLocation('');
              setSelectedAssets([]);
            }}
            sx={{
              fontWeight: 600,
              backgroundColor: !filterLocation ? navyBlue : 'transparent',
              color: !filterLocation ? 'white' : navyBlue,
              border: `1.5px solid ${navyBlue}`,
              '&:hover': {
                backgroundColor: !filterLocation ? '#2c4a6e' : 'rgba(30, 58, 95, 0.08)',
              },
            }}
          />
          {locationCounts.not_assigned > 0 && (
            <Chip
              label={`Not Assigned (${locationCounts.not_assigned})`}
              onClick={() => {
                setFilterLocation('not_assigned');
                setSelectedAssets([]);
              }}
              sx={{
                fontWeight: 600,
                backgroundColor: filterLocation === 'not_assigned' ? '#f57c00' : 'transparent',
                color: filterLocation === 'not_assigned' ? 'white' : '#f57c00',
                border: '1.5px solid #f57c00',
                '&:hover': {
                  backgroundColor: filterLocation === 'not_assigned' ? '#ef6c00' : 'rgba(245, 124, 0, 0.08)',
                },
              }}
            />
          )}
          {locations.map((location) => (
            <Chip
              key={location.id}
              label={`${location.name} (${locationCounts[location.name] || 0})`}
              onClick={() => {
                setFilterLocation(filterLocation === location.name ? '' : location.name);
                setFilterCategory('');
                setSelectedAssets([]);
              }}
              sx={{
                fontWeight: 600,
                backgroundColor: filterLocation === location.name ? navyBlue : 'transparent',
                color: filterLocation === location.name ? 'white' : navyBlue,
                border: `1.5px solid ${navyBlue}`,
                '&:hover': {
                  backgroundColor: filterLocation === location.name ? '#2c4a6e' : 'rgba(30, 58, 95, 0.08)',
                },
              }}
            />
          ))}
        </Box>
      </Paper>

      {/* Search and Category Filter */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2, border: `1px solid ${borderColor}`, '@media print': { display: 'none' } }}>
        <TextField
          fullWidth
          label="Search QR Codes"
          placeholder="Search by asset number, description, category, or location..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
        {uniqueCategories.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, color: accentBlue, fontWeight: 600 }}>
              Filter by Category
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip
                label="All Categories"
                onClick={() => {
                  setFilterCategory('');
                  setSelectedAssets([]);
                }}
                sx={{
                  fontWeight: 600,
                  backgroundColor: !filterCategory ? navyBlue : 'transparent',
                  color: !filterCategory ? 'white' : navyBlue,
                  border: `1.5px solid ${navyBlue}`,
                  '&:hover': {
                    backgroundColor: !filterCategory ? '#2c4a6e' : 'rgba(30, 58, 95, 0.08)',
                  },
                }}
              />
              {uniqueCategories.map((category) => (
                <Chip
                  key={category}
                  label={category}
                  onClick={() => {
                    setFilterCategory(filterCategory === category ? '' : category);
                    setSelectedAssets([]);
                  }}
                  sx={{
                    fontWeight: 600,
                    backgroundColor: filterCategory === category ? navyBlue : 'transparent',
                    color: filterCategory === category ? 'white' : navyBlue,
                    border: `1.5px solid ${navyBlue}`,
                    '&:hover': {
                      backgroundColor: filterCategory === category ? '#2c4a6e' : 'rgba(30, 58, 95, 0.08)',
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedAssets.length === filteredQRCodes.length && filteredQRCodes.length > 0}
                indeterminate={selectedAssets.length > 0 && selectedAssets.length < filteredQRCodes.length}
                onChange={handleSelectAll}
                sx={{
                  color: accentBlue,
                  '&.Mui-checked': { color: accentBlue },
                }}
              />
            }
            label={`Select All (${filteredQRCodes.length})`}
          />
          {(searchTerm || filterCategory || filterLocation) && (
            <Button
              size="small"
              onClick={() => {
                setSearchTerm('');
                setFilterCategory('');
                setFilterLocation('');
              }}
              sx={{ color: accentBlue }}
            >
              Clear Filters
            </Button>
          )}
        </Box>
      </Paper>

      {/* Sticky toolbar for when assets are selected */}
      {selectedAssets.length > 0 && (
        <Paper
          elevation={3}
          sx={{
            position: 'sticky',
            top: 64,
            zIndex: 1100,
            mb: 2,
            borderRadius: 2,
            overflow: 'hidden',
            '@media print': { display: 'none' }
          }}
        >
          <Toolbar sx={{ background: gradientPrimary, color: 'white' }}>
            <Typography sx={{ flex: 1 }}>
              {selectedAssets.length} QR code{selectedAssets.length !== 1 ? 's' : ''} selected
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<PrintIcon />}
              onClick={handlePrintSelected}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Print Selected
            </Button>
            <Button
              sx={{ ml: 2, borderRadius: 2, textTransform: 'none' }}
              variant="outlined"
              color="inherit"
              onClick={() => setSelectedAssets([])}
            >
              Clear Selection
            </Button>
          </Toolbar>
        </Paper>
      )}

      <div className="print-area">
        <Grid container spacing={2}>
          {filteredQRCodes.map((qr) => {
            const isOffice = isOfficeCard(qr);
            const isSelected = selectedAssets.includes(qr.id);
            const selectedClass = isSelected ? 'qr-card-selected' : '';

            const selectionBox = (
              <Box sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 2,
                '@media print': { display: 'none' }
              }}>
                <Checkbox
                  checked={isSelected}
                  onChange={() => handleToggleSelect(qr.id)}
                  size="small"
                  sx={{
                    color: accentBlue,
                    '&.Mui-checked': { color: accentBlue },
                  }}
                />
              </Box>
            );

            return (
              <Grid
                item
                xs={12} sm={6} md={4} lg={3}
                key={qr.id}
                className={[
                  isSelected ? 'qr-grid-item-selected' : 'qr-grid-item-unselected',
                  isOffice ? 'qr-grid-item-office' : ''
                ].filter(Boolean).join(' ')}
              >
                {/* Standard card — visible on screen for ALL assets; hidden on print for Office */}
                <Card
                  className={`qr-card qr-card-standard ${selectedClass}`}
                  data-asset-id={qr.id}
                  sx={{
                    pageBreakInside: 'avoid',
                    position: 'relative',
                    borderRadius: 2,
                    border: `1px solid ${borderColor}`,
                    '&:hover': {
                      boxShadow: 6,
                      transform: 'translateY(-2px)',
                      transition: 'all 0.2s ease-in-out',
                    },
                    '@media print': {
                      boxShadow: 'none',
                      border: '1px solid #ddd',
                      // For Office assets, hide the standard card so only the premium version prints
                      ...(isOffice && { display: 'none !important' }),
                    },
                  }}
                >
                  {selectionBox}
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: accentBlue }}>
                      {qr.asset_number}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontSize: '0.9rem', minHeight: '44px' }}>
                      {qr.asset_description}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500, mt: 1, color: accentBlue }}>
                      Scan to create maintenance request
                    </Typography>
                    <Box sx={{ my: 1.5 }}>
                      <img
                        src={qr.qrCode}
                        alt={`QR Code for ${qr.asset_number}`}
                        style={{ width: '100%', maxWidth: '180px' }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500, color: accentBlue }}>
                      Escanear para crear solicitud de mantenimiento
                    </Typography>
                  </CardContent>
                </Card>

                {/* Print-only premium card for Office assets */}
                {isOffice && (
                  <Card
                    className={`qr-card qr-card-print-office ${selectedClass}`}
                    data-asset-id={qr.id}
                    sx={{
                      display: 'none',
                      '@media print': {
                        display: 'flex !important',
                      },
                    }}
                  >
                    {/* Wordmark strip */}
                    <div className="qr-po-header">
                      <img
                        src="/sleeptronic-logo-white.png"
                        alt="Sleeptronic"
                        className="qr-po-logo"
                      />
                    </div>

                    {/* Content */}
                    <div className="qr-po-body">
                      <div className="qr-po-badge">{qr.asset_number}</div>
                      <div className="qr-po-title">{qr.asset_description}</div>
                      <img
                        src={qr.qrCode}
                        alt={`QR Code for ${qr.asset_number}`}
                        className="qr-po-qr"
                      />
                      <div className="qr-po-scan-en">Scan to create maintenance request</div>
                      <div className="qr-po-scan-es">Escanee para crear una solicitud de mantenimiento</div>
                    </div>
                  </Card>
                )}
              </Grid>
            );
          })}
        </Grid>
      </div>
    </Box>
  );
}
